import React, { useEffect, useState } from "react";
import { Stack, Text, Spinner, SpinnerSize, IStackStyles, ITextStyles, StackItem } from "@fluentui/react";
import { fetchVersionInfo, VersionInfo, ServiceVersionInfo } from "../../services/VersionService";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import styles from "./Version.module.css";
import { useAuth } from "react-oidc-context";
import packageJson from "../../../package.json";

// Styles are now defined in the CSS module

const headerStyles: ITextStyles = {
  root: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 20,
  },
};

const sectionHeaderStyles: ITextStyles = {
  root: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 20,
    marginBottom: 10,
  },
};

const infoItemStyles: ITextStyles = {
  root: {
    fontSize: 14,
    marginBottom: 5,
  },
};

const statusStyles = (status: string): React.CSSProperties => {
  if (status.startsWith("Error") || status.includes("refused")) {
    return { color: "red" };
  } else if (status === "Endpoint not configured") {
    return { color: "orange" };
  } else {
    return { color: "green" };
  }
};

const Version: React.FC = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const auth = useAuth();

  useEffect(() => {
    if (auth.isLoading) return;
    if (!auth.user?.id_token) return;

    const getVersionInfo = async () => {
      try {
        setLoading(true);
        const response = await fetchVersionInfo(auth.user?.id_token);

        if (response.data) {
          setVersionInfo(response.data);
          setError(undefined); // Clear error if fetch succeeds
        } else if (response.error) {
          setError(response.error);
        } else {
          setError("Failed to fetch version information");
        }
      } catch (err) {
        setError("An error occurred while fetching version information");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getVersionInfo();
  }, [auth.isLoading, auth.user?.id_token]);

  const formatDate = (dateString: string) => {
    try {
      if (!dateString || dateString.trim() === '') {
        return "Unknown";
      }
      
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (Number.isNaN(date.getTime())) {
        return "Unknown";
      }
      
      // Format as DD/MM/YYYY HH:MM:SS (UTC)
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const seconds = date.getUTCSeconds().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} (UTC)`;
    } catch (err) {
      return "Unknown";
    }
  };

  const renderServiceInfo = (title: string, service: ServiceVersionInfo) => {
    // Create a kebab-case version of the title for use in data-testid
    const titleId = title.toLowerCase().replaceAll(/\s+/g, '-');
    
    return (
      <Stack>
        <Text styles={sectionHeaderStyles}>{title}</Text>
        <Text styles={infoItemStyles} data-testid={`${titleId}-version`}>Version: {service.version}</Text>
        <Text styles={infoItemStyles} data-testid={`${titleId}-build-date`}>Build Date: {service.buildDate !== "Unknown" ? formatDate(service.buildDate) : "Unknown"}</Text>
        <Text styles={infoItemStyles} style={statusStyles(service.status)} data-testid={`${titleId}-status`}>Status: {service.status}</Text>
      </Stack>
    );
  };

  return (
    <Stack className={styles.mainLayout}>
      <StackItem>
        <Header />
      </StackItem>
      <StackItem className={styles.mainContent}>
        <div className={styles.versionContainer}>
          <Text styles={headerStyles}>System Version Information</Text>

          {error && !loading && (
            <Stack style={{ marginBottom: 10 }}>
              <Text style={{ color: "red" }}>{error}</Text>
            </Stack>
          )}

          {loading && (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ padding: 20 }}>
              <Spinner size={SpinnerSize.large} label="Loading version information..." />
            </Stack>
          )}

          {versionInfo && !loading && (
            <>
              <Stack>
                <Text styles={sectionHeaderStyles}>Frontend Application</Text>
                <Text styles={infoItemStyles} data-testid="frontend-version">Version: {packageJson.version}</Text>
                <Text styles={infoItemStyles} data-testid="frontend-build-date">Build Date: {formatDate(__BUILD_DATE__)}</Text>
              </Stack>

              <Stack>
                <Text styles={sectionHeaderStyles}>Core Application</Text>
                <Text styles={infoItemStyles} data-testid="core-version">Version: {versionInfo.core.appVersion}</Text>
                <Text styles={infoItemStyles} data-testid="core-build-date">Build Date: {formatDate(versionInfo.core.appBuildDate)}</Text>
                <Text styles={infoItemStyles} style={statusStyles(versionInfo.core.status)} data-testid="core-status">Status: {versionInfo.core.status}</Text>
              </Stack>

              {renderServiceInfo("Document Generator", versionInfo.docGenerator)}
              {renderServiceInfo("Admin Service", versionInfo.admin)}
              {renderServiceInfo("Importer Service", versionInfo.importer)}

              {/* Cache Store Section */}
              {versionInfo.cacheStore && (
                <Stack>
                  <Text styles={sectionHeaderStyles}>Cache Store</Text>
                  {Object.entries(versionInfo.cacheStore).map(([key, value]) => {
                    const isFallback = key.endsWith("_fallback");

                    const humanizeKey = (rawKey: string) => {
                      const base = rawKey.replace(/_fallback$/, "");
                      const parts = base.split("_");
                      // Keep the first acronym as-is, make the rest lowercase for readability
                      const labelCore = parts
                        .map((p, idx) => (idx === 0 ? p : p.toLowerCase()))
                        .join(" ");
                      return isFallback ? `${labelCore} last update` : `${labelCore} expires at`;
                    };

                    const label = humanizeKey(key);

                    const displayValue = () => {
                      if (!value || value === "Unknown") return "Unknown";
                      return  formatDate(value);
                    };

                    const testId = `cache-${key.toLowerCase().replaceAll('_', '-')}`;

                    return (
                      <Text key={key} styles={infoItemStyles} data-testid={testId}>
                        {label}: {displayValue()}
                      </Text>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </div>
      </StackItem>
      <StackItem>
        <Footer />
      </StackItem>
    </Stack>
  );
};

export default Version;
